import type { User } from "oidc-client-ts";
// Regel 1 i BRAND_RULES: bruk de leverte logofilene. Aldri tegne, sette,
// fargelegge eller generere dem på nytt. Vite bunter dem med innholdshash,
// så de serveres fra egen origin og CSP-en (img-src 'self') holder.
import logoUrl from "@actiwise/brand/assets/logo-horizontal-360.png";
import tomIllustrasjon from "@actiwise/brand/assets/illustration-empty-900.png";
import { tilgjengeligeApper } from "./apps";
import { profilFra, rollerFra } from "./claims";
import { kontoUrl } from "./config";
import { loggInn, loggUt } from "./auth";
import { el, rot } from "./dom";
import { STATUS_STI } from "./status";

export type SesjonsStatus =
  | { type: "aktiv"; fornyetKl: Date | null }
  | { type: "fornyer" }
  | { type: "utlopt" };

let sisteBruker: User | null = null;
let sisteStatus: SesjonsStatus = { type: "aktiv", fornyetKl: null };

/** Regel 9: hver tilstand skal finnes. Denne vises mens vi sjekker om det
 *  ligger en gyldig sesjon, før vi vet hva som skal tegnes. */
export function visLaster(tekst = "Laster …"): void {
  const node = rot();
  const boks = el("div", "laster");
  const snurr = el("div", "spinner");
  snurr.setAttribute("aria-hidden", "true");
  const status = el("p", undefined, tekst);
  status.setAttribute("role", "status");
  boks.append(snurr, status);
  node.append(boks);
}

export function visInnlogging(): void {
  const node = rot();
  const kort = el("div", "kort");
  const logo = el("img", "logo-stor");
  logo.src = logoUrl;
  logo.alt = "ActiWise";
  kort.append(
    logo,
    el("p", "dempet", "Logg inn for å se appene du har tilgang til."),
  );

  const knapp = el("button", "primaer", "Logg inn");
  knapp.addEventListener("click", startInnlogging);
  kort.append(knapp);
  node.append(kort, bunntekst());
}

export function visFeil(overskrift: string, detalj: string): void {
  const node = rot();
  const kort = el("div", "kort");
  kort.append(el("h1", undefined, overskrift), el("p", "dempet", detalj));

  const knapp = el("button", "primaer", "Prøv å logge inn på nytt");
  knapp.addEventListener("click", startInnlogging);
  // Feiler innloggingen, er det første spørsmålet om det er hos oss.
  const status = statusLenke("sekundaer", "Se driftsstatus");
  const handlinger = el("div", "handlinger");
  handlinger.append(knapp, status);
  kort.append(handlinger);
  node.append(kort);
}

/**
 * signinRedirect henter discovery-dokumentet før den sender brukeren videre.
 * Er IdP-en nede, feiler det her - og uten denne fangsten skjedde ingenting
 * når brukeren trykket på knappen.
 */
function startInnlogging(): void {
  loggInn().catch((feil: unknown) => {
    console.error("Innloggingen kunne ikke starte:", feil);
    visFeil(
      "Fikk ikke kontakt med innloggingen",
      "Innloggingstjenesten svarer ikke akkurat nå. Se driftsstatus, eller prøv igjen om litt.",
    );
  });
}

function statusLenke(klasse: string, tekst: string): HTMLAnchorElement {
  const lenke = el("a", klasse, tekst);
  lenke.href = STATUS_STI;
  return lenke;
}

function bunntekst(): HTMLElement {
  const bunn = el("footer", "bunn liten");
  bunn.append(statusLenke("dempet", "Driftsstatus"));
  return bunn;
}

export function settSesjonsStatus(status: SesjonsStatus): void {
  sisteStatus = status;
  if (sisteBruker) visPortal(sisteBruker);
}

export function visPortal(bruker: User): void {
  sisteBruker = bruker;
  const node = rot();
  const profil = profilFra(bruker);
  const roller = rollerFra(bruker);
  const mine = tilgjengeligeApper(roller);

  // --- Topplinje ---
  const topp = el("header", "topp");
  const venstre = el("div");
  const logo = el("img", "logo");
  logo.src = logoUrl;
  logo.alt = "ActiWise";
  venstre.append(logo);

  const hoyre = el("div", "topp-hoyre");
  const bruker_ = el("div", "bruker");
  bruker_.append(el("span", "navn", profil.navn));
  if (profil.epost) bruker_.append(el("span", "dempet liten", profil.epost));
  hoyre.append(bruker_, statusMerke(), kontoLenke(), utloggKnapp());
  topp.append(venstre, hoyre);

  // --- Fliser ---
  const main = el("main");
  if (mine.length === 0) {
    const tomt = el("div", "kort");
    const bilde = el("img", "illustrasjon");
    bilde.src = tomIllustrasjon;
    bilde.alt = "";
    tomt.append(
      bilde,
      el("h2", undefined, "Ingen apper tilgjengelig"),
      el(
        "p",
        "dempet",
        "Kontoen din er ikke meldt inn i noen appgruppe ennå. Ta kontakt med " +
          "administrator, så blir du lagt til i riktig gruppe.",
      ),
    );
    main.append(tomt);
  } else {
    const rutenett = el("div", "rutenett");
    for (const app of mine) rutenett.append(flis(app));
    main.append(rutenett);
  }

  node.append(topp, main, bunntekst());
}

function flis(app: ReturnType<typeof tilgjengeligeApper>[number]): HTMLElement {
  const lenke = el("a", "flis");
  lenke.href = app.url;
  const merke = el("img", "merke");
  merke.src = app.merke;
  // Dekorativt: appnavnet står i klartekst rett under, og et alt-attributt
  // her ville bare fått skjermleseren til å lese navnet to ganger. Merkene
  // skiller appene på form, ikke farge, så ingenting går tapt uten bildet.
  merke.alt = "";
  merke.width = 48;
  merke.height = 48;
  lenke.append(
    merke,
    el("span", "flis-navn", app.navn),
    el("span", "dempet liten", app.beskrivelse),
  );
  return lenke;
}

function statusMerke(): HTMLElement {
  switch (sisteStatus.type) {
    case "fornyer":
      return el("span", "status fornyer", "Fornyer sesjon…");
    case "utlopt":
      return el("span", "status utlopt", "Sesjonen er utløpt");
    case "aktiv": {
      const kl = sisteStatus.fornyetKl;
      const tekst = kl
        ? `Aktiv · fornyet ${kl.toLocaleTimeString("no-NO", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        : "Aktiv";
      return el("span", "status aktiv", tekst);
    }
  }
}

function kontoLenke(): HTMLElement {
  // Passord og MFA styrer brukeren selv i Keycloaks account console.
  const lenke = el("a", "sekundaer", "Konto og MFA");
  lenke.href = kontoUrl;
  lenke.rel = "noopener";
  return lenke;
}

function utloggKnapp(): HTMLElement {
  const knapp = el("button", "sekundaer", "Logg ut");
  knapp.addEventListener("click", () => {
    void loggUt();
  });
  return knapp;
}
